import axios from 'axios';

// Public API client - no auth interceptors needed for lead capture
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

export interface LeadContactSubmit {
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  school_name?: string;
  role?: string;
  num_children?: number;
  province?: string;
  message?: string;
  source?: string;
  campaign?: string;
  email_consent: boolean;
  website_url?: string; // honeypot
}

export interface LeadNewsletterSubmit {
  email: string;
  first_name?: string;
  email_consent: boolean;
  source?: string;
  campaign?: string;
  website_url?: string; // honeypot
}

export interface LeadSubmitResponse {
  id: string;
  email: string;
  status: string;
}

export interface Lead {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  school_name: string | null;
  role: string | null;
  num_children: number | null;
  province: string | null;
  source: string;
  campaign: string | null;
  form_type: string;
  status: string;
  notes: string | null;
  message: string | null;
  created_date: string;
  modified_date: string;
}

export interface LeadActivity {
  id: string;
  lead_id: string;
  activity_type: string;
  description: string | null;
  old_value: string | null;
  new_value: string | null;
  created_date: string;
}

export interface LeadDetail extends Lead {
  activities: LeadActivity[];
}

export interface LeadUpdate {
  status?: string;
  notes?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  school_name?: string;
  role?: string;
  num_children?: number;
  province?: string;
}

const BASE_URL = '/leads';

export const leadsApi = {
  // Public endpoints
  async submitContact(data: LeadContactSubmit): Promise<LeadSubmitResponse> {
    const response = await publicApiClient.post(`${BASE_URL}/contact`, data);
    return response.data;
  },

  async submitNewsletter(data: LeadNewsletterSubmit): Promise<LeadSubmitResponse> {
    const response = await publicApiClient.post(`${BASE_URL}/newsletter`, data);
    return response.data;
  },

  // Admin endpoints (use authenticated client)
  async getLeads(params?: {
    status?: string;
    form_type?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<Lead[]> {
    const { apiClient } = await import('./client');
    const response = await apiClient.get(`${BASE_URL}/`, { params });
    return response.data;
  },

  async getLead(id: string): Promise<LeadDetail> {
    const { apiClient } = await import('./client');
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  async updateLead(id: string, data: LeadUpdate): Promise<Lead> {
    const { apiClient } = await import('./client');
    const response = await apiClient.put(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  async deleteLead(id: string): Promise<void> {
    const { apiClient } = await import('./client');
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  async convertLead(id: string, data: { org_name: string; admin_email: string }): Promise<{ organization_id: string }> {
    const { apiClient } = await import('./client');
    const response = await apiClient.post(`${BASE_URL}/${id}/convert`, data);
    return response.data;
  },
};
